import React, { useState, useEffect, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { Layout, theme } from 'antd';

import config, { Product } from 'app.config';
import { FIFTY_MINUTE_TIMER_INTERVAL } from 'constants/index';
import { useVerifyUser } from 'hooks/login';
import { useLogout } from 'hooks/login';

import RouteWrapper from '../RouteWrapper';
import Loading from '../Loading';
import HeaderAccount from './HeaderAccount';
import NavSidebar from './NavSidebar';
import Marquee from './Marquee';
import { getUserToken } from 'api/login';
import { ShopeeLogo } from 'icons';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { UserContext } from 'routes/login/userContext';
import AnnouncementPopup from 'components/AnnouncementPopup';

const { useToken } = theme;

const { reactAppVersion, product } = config;
const { Header, Content, Sider } = Layout;

const HeaderHeight = 64;

function LayoutWrapper() {
  const { token } = useToken();
  const [collapsed, setCollapsed] = useState(false);
  const [counter, setCounter] = useState(0);
  const { logout } = useLogout();
  const userContext = useContext(UserContext);

  const {
    isVerifying,
    user: { firebaseUser },
  } = useVerifyUser();

  // Note: Removed old announcement popup hook system - now uses new dual-API architecture
  // The new AnnouncementPopup component handles all logic internally

  // Note: Removed SubAccountLocked functionality - now handled within AnnouncementPopup component
  useEffect(() => {
    const timer = setInterval(async () => {
      await getUserToken();
      setCounter((v) => v + 1);
    }, FIFTY_MINUTE_TIMER_INTERVAL);
    return () => {
      clearInterval(timer);
    };
  }, [counter]);

  if (isVerifying) {
    return <></>;
  }

  return (
    <Layout>
      <Header
        style={{
          position: 'fixed',
          width: '100vw',
          padding: '0 24px',
          top: 0,
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'white',
          boxShadow: '0 0 9px 0 rgba(0, 0, 0, 0.12)',
        }}
      >
        <NavLink to={process.env.PUBLIC_URL}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ShopeeLogo />
            <div
              style={{
                color: token.colorPrimary,
                marginLeft: token.marginXS,
                fontWeight: 700,
                fontSize: token.fontSizeXL,
              }}
            >
              {product}
            </div>
          </div>
        </NavLink>
        <Marquee />
        <HeaderAccount user={firebaseUser} signOut={logout} />
      </Header>
      <Layout>
        <Sider
          style={{
            position: 'fixed',
            overflow: 'auto',
            height: '100vh',
            zIndex: 10,
            left: 0,
            top: HeaderHeight,
          }}
          theme="light"
          width="200px"
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          collapsedWidth={80}
          trigger={
            <div>
              {collapsed ? (
                <RightOutlined style={{ color: token.colorTextSecondary }} />
              ) : (
                <LeftOutlined style={{ color: token.colorTextSecondary }} />
              )}
            </div>
          }
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: `calc(100vh - ${HeaderHeight}px)`,
            }}
          >
            <NavSidebar userVerified={!!firebaseUser} />

            <small
              style={{
                margin: '0 16px',
                marginBottom: '48px',
                paddingBottom: '12px',
                textAlign: 'center',
                color: token.colorTextTertiary,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              {`v${reactAppVersion}`}
            </small>
          </div>
        </Sider>
        <Layout
          style={{
            minHeight: `calc(100vh - ${HeaderHeight}px)`,
            marginTop: '64px',
            marginLeft: collapsed ? '80px' : '200px',
            transition: '0.25s ease',
          }}
        >
          <Content
            style={{
              backgroundColor: '#F5F5F5',
              padding: `${token.paddingLG}px`,
            }}
          >
            {!!firebaseUser ? <RouteWrapper /> : <Loading />}
          </Content>
        </Layout>
      </Layout>

      {/* Note: Sub-account locked screen removed - functionality now handled within AnnouncementPopup flow */}
      {/* In the new architecture, sub-account restrictions are handled by the AnnouncementPopup component */}

      {/* Announcement popups - New dual-API architecture (only for suppliers management) */}
      {product === Product.SuppliersManagement && firebaseUser && userContext?.id && (
        <AnnouncementPopup
          onTriggerLogout={logout}
          onComplete={() => {
            // Popup flow completed - can add any completion logic here if needed
            console.log('Announcement popup flow completed');
          }}
        />
      )}
    </Layout>
  );
}

export default LayoutWrapper;
